import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_INK, formatUsdCompact, formatUsdFull } from './common';

export interface AreaStackSeries {
  dataKey: string;
  name: string;
  color: string;
}

interface AreaStackChartProps {
  data: Array<Record<string, number | string>>;
  series: AreaStackSeries[];
  xKey?: string;
  valueFormatter?: (value: number) => string;
}

/**
 * Stacked area over time — for compositions where the running total matters as
 * much as the split (fee revenue by source). Soft gradient fills keep the
 * stacked bands legible where flat blocks of similar blue would merge.
 */
const AreaStackChart: React.FC<AreaStackChartProps> = ({
  data,
  series,
  xKey = 'label',
  valueFormatter = formatUsdFull,
}) => (
  <ResponsiveContainer width="100%" height="100%">
    <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
      <defs>
        {series.map((s) => (
          <linearGradient key={s.dataKey} id={`area-${s.dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={s.color} stopOpacity={0.55} />
            <stop offset="100%" stopColor={s.color} stopOpacity={0.08} />
          </linearGradient>
        ))}
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke={CHART_INK.grid} vertical={false} />
      <XAxis
        dataKey={xKey}
        tick={{ fontSize: 11, fill: CHART_INK.muted }}
        tickLine={false}
        axisLine={false}
        minTickGap={20}
      />
      <YAxis
        tickFormatter={formatUsdCompact}
        tick={{ fontSize: 11, fill: CHART_INK.muted }}
        tickLine={false}
        axisLine={false}
        width={64}
      />
      <Tooltip
        formatter={(value: number, name: string) => [valueFormatter(value), name]}
        contentStyle={{ borderRadius: 10, border: `1px solid ${CHART_INK.grid}`, fontSize: 12 }}
      />
      <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
      {series.map((s) => (
        <Area
          key={s.dataKey}
          type="monotone"
          dataKey={s.dataKey}
          name={s.name}
          stackId="fees"
          stroke={s.color}
          strokeWidth={2}
          fill={`url(#area-${s.dataKey})`}
          isAnimationActive={false}
        />
      ))}
    </AreaChart>
  </ResponsiveContainer>
);

export default AreaStackChart;
